import React, { useEffect, useState } from "react";
import { X, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

// Modal popup that lets a visitor subscribe without leaving the page.
// Triggered by the "Subscribe" button in the Navbar.
export default function SubscribeModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // Close on Escape, and reset state when closed
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      // Give the close animation a beat before resetting
      const t = setTimeout(() => {
        setEmail("");
        setDone(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/subscribe", { email });
      setDone(true);
      setEmail("");
      if (data.already_subscribed) {
        toast.success("You're already on the list. Thank you.");
      } else {
        toast.success("Welcome — encouragement is on its way.");
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Could not subscribe. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="subscribe-modal-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Subscribe to B. Ruff Writes"
        data-testid="subscribe-modal"
        onClick={(e) => e.stopPropagation()}
        className="relative bg-paper max-w-md w-full border border-rule shadow-xl"
      >
        <button
          onClick={onClose}
          aria-label="Close subscribe form"
          data-testid="subscribe-modal-close"
          className="absolute top-3 right-3 p-2 text-ink-soft hover:text-terracotta transition-colors"
        >
          <X size={18} />
        </button>

        <div className="p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-5">
            <Mail size={18} className="text-terracotta" />
            <p className="text-xs uppercase tracking-[0.22em] text-ink-soft">
              Encouragement in your inbox
            </p>
          </div>

          <h2 className="font-serif-display text-2xl sm:text-3xl text-ink leading-snug mb-3">
            Join the quiet circle.
          </h2>
          <p className="text-sm text-ink-soft leading-relaxed mb-6">
            Now and then — a poem, a short essay, or news of a new piece.
            No spam, only quiet words.
          </p>

          {done ? (
            <p
              data-testid="subscribe-modal-confirmation"
              className="font-serif-display italic text-terracotta text-lg"
            >
              Thank you for letting me in.
            </p>
          ) : (
            <form onSubmit={submit} data-testid="subscribe-modal-form" className="flex flex-col gap-3">
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="subscribe-modal-email-input"
                className="w-full bg-transparent border-b border-rule focus:border-ink py-2.5 text-sm text-ink outline-none"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="subscribe-modal-submit-btn"
                className="w-full px-5 py-3 bg-ink text-paper text-xs tracking-[0.2em] uppercase hover:bg-terracotta transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : "Subscribe"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
